import {
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { ChangeDetectorRef, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'voice-recorder',
    templateUrl: './voice-recorder.component.html',
    styleUrls: ['./voice-recorder.component.scss'],
})
export class VoiceRecorderComponent implements OnInit, OnDestroy, OnChanges {
    recognition: any;
    isRecording = false;
    fullText = '';
    interimText = '';
    selectedLanguage = 'es-ES';
    continuousMode = true;
    flagPlugin = false;

    private silenceTimer: any;
    private transcriptSubject = new Subject<string>();
    debounceTimeTexEmmiter: number;

    @Input() readonly silenceThreshold: number = 1500;
    @Input() set initialText(value: string) {
        if (value && !this.isRecording && !this.fullText) {
            this.fullText = value;
            this.cdr.detectChanges();
        }
    }

    @Output() transcript = new EventEmitter<string>();

    @ViewChild('textAreaRef') textAreaRef!: ElementRef<HTMLTextAreaElement>;
    @Input() showTextArea: boolean = true;
    @Input() showBorders: boolean = true;
    @Input() showLanguageSelect: boolean = true;
    @Input() maxChars?: number;

    private lastTranscript = '';
    private isStarting = false;

    constructor(private translate: TranslateService, private cdr: ChangeDetectorRef, private ngZone: NgZone) {
        this.debounceTimeTexEmmiter = this.silenceThreshold + 50;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.silenceThreshold) this.debounceTimeTexEmmiter = this.silenceThreshold + 50;

        if (changes.maxChars && this.maxChars) {
            this.enforceMaxLength();
        }
    }

    ngOnInit(): void {
        this.initRecognition();

        this.transcriptSubject
            .pipe(debounceTime(this.debounceTimeTexEmmiter), distinctUntilChanged())
            .subscribe((text) => {
                const trimmed = text.trim();
                if (trimmed !== this.lastTranscript) {
                    this.transcript.emit(trimmed);
                    this.lastTranscript = trimmed;
                    this.fullText = '';
                    this.interimText = '';
                }
            });
    }

    ngOnDestroy(): void {
        this.stopRecording();
        this.transcriptSubject.complete();
    }

    initRecognition(): void {
        const SpeechRecognition = window['SpeechRecognition'] || window['webkitSpeechRecognition'];
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (!SpeechRecognition || isMobile) {
            this.flagPlugin = true;
            console.error('No se soporta reconocimiento de voz');
            return;
        }

        this.recognition = new SpeechRecognition();
        this.recognition.continuous = this.continuousMode;
        this.recognition.interimResults = true;
        this.recognition.lang = this.selectedLanguage;

        this.recognition.onstart = () => {
            this.ngZone.run(() => {
                this.isRecording = true;
                this.interimText = '';
            });
        };

        this.recognition.onresult = (event) => {
            this.resetSilenceTimer();

            this.ngZone.runOutsideAngular(() => {
                let interim = '';
                let final = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        final += transcript + ' ';
                    } else {
                        interim += transcript;
                    }
                }

                if (final) {
                    if (this.fullText.length > 0 && !this.fullText.endsWith(' ')) {
                        this.fullText += ' ';
                    }
                    this.fullText += final;
                }

                this.interimText = interim;

                this.ngZone.run(() => {
                    const fullTranscript = this.fullText + this.interimText;

                    if (this.maxChars !== undefined && fullTranscript.length >= this.maxChars) {
                        this.fullText = fullTranscript.slice(0, this.maxChars);
                        this.interimText = '';
                        this.stopRecording();
                    }

                    this.transcriptSubject.next(this.fullText + this.interimText);
                });

                requestAnimationFrame(() => {
                    const el = this.textAreaRef?.nativeElement;
                    if (el) el.scrollTop = el.scrollHeight;
                });
            });
        };

        this.recognition.onerror = (event) => {
            console.error('Error reconociendo voz:', event.error);
            this.ngZone.run(() => this.stopRecording());
        };

        this.recognition.onend = () => {
            this.ngZone.run(() => {
                if (this.isRecording && this.continuousMode) {
                    this.recognition.start();
                } else {
                    this.stopRecording();
                }
            });
        };
    }

    toggleRecording(): void {
        if (this.isRecording) {
            this.stopRecording();
        } else {
            if (!this.recognition) {
                this.initRecognition();
            }

            if (!this.isStarting) {
                this.isStarting = true;
                try {
                    this.recognition.start();
                } catch (e) {
                    console.warn('Reconocimiento ya iniciado.');
                } finally {
                    this.isStarting = false;
                }
            }
        }
    }

    stopRecording(): void {
        this.isRecording = false;
        clearTimeout(this.silenceTimer);
        if (this.recognition) {
            try {
                this.recognition.stop();
            } catch (e) {
                console.warn('Reconocimiento de voz detenido.');
            }
        }
        this.interimText = '';
    }

    onLanguageChange(): void {
        if (this.recognition) {
            this.recognition.lang = this.selectedLanguage;
        }
    }

    onModeChange(checked: boolean): void {
        this.continuousMode = checked;
        if (this.recognition) {
            this.recognition.continuous = checked;
        }
    }

    onTextAreaInput(): void {
        const composedText = this.fullText + this.interimText;
        if (this.maxChars !== undefined && composedText.length > this.maxChars) {
            this.fullText = composedText.slice(0, this.maxChars);
            this.interimText = '';
        }
        this.transcriptSubject.next(this.fullText + this.interimText);
    }

    private resetSilenceTimer(): void {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = setTimeout(() => {
            this.ngZone.run(() => {
                this.stopRecording();
                console.log('Grabación detenida por inactividad.');
            });
        }, this.silenceThreshold);
    }

    private enforceMaxLength(): void {
        const composedText = this.fullText + this.interimText;
        if (this.maxChars !== undefined && composedText.length > this.maxChars) {
            this.fullText = composedText.slice(0, this.maxChars);
            this.interimText = '';
            this.cdr.detectChanges();
        }
    }
}
